import { Component, forwardRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ErrorMessages, Permissions } from '../../common/constants';
import { Alert } from '../../model';
import { AlertDefinitionService } from '../../service/alert-definition.service';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { ThingContextService } from '../../service/thing-context.service';
import { ThingService } from '../../service/thing.service';
import { TicketService } from '../../service/ticket.service';
import { UiService } from '../../service/ui.service';
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../../widget/shared/snackbar.service';
import { TicketListDialogComponent } from '../../widget/ticket-list/ticket-list-dialog.component';
import { AlertWorkSessionDetailsPageDialogComponent } from '../alert-work-session-details-page-dialog/alert-work-session-details-page-dialog.component';
import { AlertService } from '../shared/alert.service';

@Component({
    selector: 'event-details-page-dialog',
    template: `
        <h2 mat-dialog-title class="d-flex align-items-start">
            <div *ngIf="element" class="d-flex flex-column">
                <div>
                    <span [innerHTML]="(element?.severity | loader:'alertSeverity')"></span><span *ngIf="element?.title" style="margin-left: 5px" [custom-label]="element.title"></span>
                </div>
            </div>
            <button mat-icon-button mat-dialog-close class="ml-auto">
                <mat-icon>close</mat-icon>
            </button>
        </h2>
        <mat-dialog-content>
            <div *ngIf="element" class="d-flex flex-column" style="gap: 24px; margin-bottom: 24px">
                <details-page-dialog-breadcrumb [customer]="element.customer" [location]="element.location" [thing]="element.thing"></details-page-dialog-breadcrumb>
                <div *ngIf="element.description" class="details-page-dialog-description" [custom-label]="element.description"></div>
            </div>
            <alert-work-session-details-page-dialog-content *ngIf="templateName && loaded" [templateName]="templateName" [thingDefinitionId]="thingDefinitionId"></alert-work-session-details-page-dialog-content>
            <form *ngIf="element && isUserReported && (element.notes || canWriteNotes)" #noteForm="ngForm">
                <div #noteElement class="form-group">
                    <label custom-label="notesProperty"></label>
                    <div class="d-flex">
                        <textarea [ngModel]="element.notes" name="content" type="text" class="form-control flex-fill" rows="3" maxlength="1000" [disabled]="!canWriteNotes"></textarea>
                    </div>
                </div>
            </form>
        </mat-dialog-content>
        <mat-dialog-actions class="justify-content-end" style="gap: 10px">
            <mat-progress-bar *ngIf="updating" mode="indeterminate"></mat-progress-bar>
            <ng-container *ngIf="!updating">
                <button *ngIf="showTicketButton" mat-icon-button [matTooltip]="'Tickets' | localize"
                    class="btn-ticket" (click)="openTicketList()">
                    <mat-icon class="material-symbols-outlined">confirmation_number</mat-icon>
                </button>
                <button *ngIf="isUserReported && canWriteNotes" class="btn btn-primary" (click)="saveNotes()" custom-label="saveButton"
                    [disabled]="!noteForm?.form.dirty"></button>
            </ng-container>
        </mat-dialog-actions>
    `
})
export class EventDetailsPageDialogComponent extends AlertWorkSessionDetailsPageDialogComponent<Alert> implements OnInit, OnDestroy {

    @ViewChild('noteForm') noteForm: NgForm;

    showTicketButton: boolean;
    canWriteNotes: boolean;
    isUserReported: boolean;
    updating: boolean;

    private data: any;

    constructor(
        @Inject(forwardRef(() => UiService)) uiService: UiService,
        @Inject(forwardRef(() => ThingService)) thingService: ThingService,
        @Inject(forwardRef(() => ContextService)) context: ContextService,
        @Inject(forwardRef(() => ThingContextService)) thingContextService: ThingContextService,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<EventDetailsPageDialogComponent>,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => TicketService)) private ticketService: TicketService,
        @Inject(forwardRef(() => AlertDefinitionService)) private alertDefinitionService: AlertDefinitionService,
        @Inject(forwardRef(() => AlertService)) private alertService: AlertService,
        @Inject(forwardRef(() => SnackbarService)) private snackbarService: SnackbarService
    ) {
        super(uiService, thingService, context, thingContextService, data);
        this.data = data;
    }

    ngOnInit() {
        this.saveCurrentContext();
        this.init(this.elementId);
    }

    ngOnDestroy(): void {
        this.resetContext();
    }

    init(alertId: string): void {
        this.getElementById(alertId).then(alert => {
            this.element = alert;
            this.thingDefinitionId = this.element.thing?.thingDefinitionId;
            this.templateName = this.element.templateName;
            if (!this.templateName) {
                this.thingDefinitionId = null;
                this.templateName = this.uiService.getEventDetailsTemplate();
            }
            this.updateContext(this.element.thing.id, 'Alerts');
            this.handleTicketButtonVisiblity();
            if (this.element.alertDefinitionId) {
                this.alertDefinitionService.getAlertDefinitionById(this.element.alertDefinitionId).then(def => this.isUserReported = def.manualActivation).catch(() => {
                    /* do nothing */
                });
            }
        });
        this.canWriteNotes = this.authenticationService.hasPermission(Permissions.WRITE_ALERT_STATUS);
    }

    protected getElementById(elementId: string): Promise<Alert> {
        return this.thingService.getAlertById(elementId).catch(() => this.thingService.getHistoricalAlertById(elementId));
    }

    protected resetElementContext(): void {
        this.thingContextService.resetCurrentAlert();
    }

    private handleTicketButtonVisiblity(): void {
        this.ticketService.getTicketTypes().then(types => {
            this.showTicketButton = !this.data.ticketButtonDisabled && !!this.element.thing?.id && this.authenticationService.hasPermission(Permissions.READ_TICKET) && types.some(t => t.eventSelectionEnabled) && [2, 3, 4].includes(this.element.eventSeverity);
        });
    }

    openTicketList() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.data = { thingId: this.element.thing.id, eventId: this.element.id };
        firstValueFrom(this.dialog.open(TicketListDialogComponent, dialogConfig).afterClosed());
    }

    saveNotes(): void {
        this.updating = true;
        const formValue = this.noteForm?.form.getRawValue();
        this.alertService.patchNotes(this.element, formValue.content).then(() => {
            this.updating = false;
            this.dialogRef.close();
        }).catch(err => {
            this.snackbarService.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR), 'notification-error');
            this.updating = false;
        });
    }
}